import React, { useEffect, useRef, useState } from "react";
import "./LandingPage.css";
import { getSurveyStructure, submitSurveyResponse } from "../Api/SurveyApi";
import {
  captureTracking,
  getTracking,
  getSubmissionId,
  clearSubmissionId,
} from "../utils/tracking";

const SURVEY_ID = "a2IRT000003NxyX2AS";
const STAGE_1_ID = "a2JRT000001M1Pt2AK";

// Flow: screening (all questions on one form) -> patient info -> consent -> done
const STEPS = [
  { key: "screening", label: "Screening" },
  { key: "info", label: "Your info" },
  { key: "consent", label: "Consent" },
  { key: "done", label: "Done" },
];

const detectLanguage = () => {
  const browserLang = navigator.language || navigator.userLanguage || "en";
  return browserLang.toLowerCase().startsWith("es") ? "es" : "en";
};

// Renders a typed name into a PNG data URL styled like a signature, so
// the API always receives a consistent image regardless of which method
// (draw vs. type) the person used.
const renderTypedSignatureToDataUrl = async (text) => {
  const canvas = document.createElement("canvas");
  canvas.width = 640;
  canvas.height = 160;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  try {
    await document.fonts.load('600 48px "Caveat"');
  } catch {
    // If the font can't be loaded in time, canvas falls back to its
    // generic cursive font below — still produces a usable image.
  }

  ctx.fillStyle = "#1d2b27";
  ctx.font = '600 48px "Caveat", "Brush Script MT", cursive';
  ctx.textBaseline = "middle";
  ctx.fillText(text, 24, canvas.height / 2 + 8, canvas.width - 48);

  return canvas.toDataURL("image/png");
};

// A quiet pulse-line motif ties the header back to the clinical subject
// matter without leaning on a generic gradient or blob.
const PulseLine = () => (
  <svg
    className="hero-pulse"
    viewBox="0 0 600 56"
    preserveAspectRatio="none"
    aria-hidden="true"
  >
    <polyline
      points="0,30 90,30 105,10 120,46 138,30 180,30 195,18 210,40 225,30 600,30"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    />
  </svg>
);

const Stepper = ({ activeIndex }) => (
  <div className="stepper" role="list" aria-label="Survey progress">
    {STEPS.map((step, i) => (
      <React.Fragment key={step.key}>
        <div
          role="listitem"
          className={
            "stepper-item" +
            (i === activeIndex ? " is-active" : i < activeIndex ? " is-done" : "")
          }
        >
          <span className="stepper-dot">{i < activeIndex ? "✓" : i + 1}</span>
          <span className="stepper-label">{step.label}</span>
        </div>
        {i < STEPS.length - 1 && (
          <div className={"stepper-track" + (i < activeIndex ? " is-filled" : "")} />
        )}
      </React.Fragment>
    ))}
  </div>
);

const ChevronRight = () => (
  <svg className="btn-icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M6 3.5L11 8l-5 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronLeft = () => (
  <svg className="btn-icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M10 3.5L5 8l5 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ValidationMessage = ({ children }) => (
  <div className="validation-message" role="alert" aria-live="assertive">
    <span className="validation-icon" aria-hidden="true">
      !
    </span>
    {children}
  </div>
);

// Plain HTML5 canvas signature pad — no external dependency.
// Exposes the drawn signature as a PNG data URL via onChange.
const SignaturePad = ({ hasError, initialDataUrl, onChange, lang }) => {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const [hasSignature, setHasSignature] = useState(Boolean(initialDataUrl));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    // Restore a previously drawn signature (e.g. after going Back then
    // forward again) so the person doesn't have to sign twice.
    if (initialDataUrl) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      img.src = initialDataUrl;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * canvas.width,
      y: ((e.clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  const handlePointerDown = (e) => {
    const canvas = canvasRef.current;
    canvas.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    lastPointRef.current = getPoint(e);
  };

  const handlePointerMove = (e) => {
    if (!drawingRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const point = getPoint(e);

    ctx.strokeStyle = "#1d2b27";
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();

    lastPointRef.current = point;
    if (!hasSignature) setHasSignature(true);
  };

  const finishStroke = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const canvas = canvasRef.current;
    onChange(canvas.toDataURL("image/png"));
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onChange("");
  };

  return (
    <div className="signature-pad-wrap">
      <canvas
        ref={canvasRef}
        className={"signature-canvas" + (hasError ? " has-error" : "")}
        width={640}
        height={160}
        role="img"
        aria-label={
          lang === "es"
            ? "Área para firmar con el dedo, mouse o lápiz óptico"
            : "Signature area — sign with your finger, mouse, or stylus"
        }
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishStroke}
        onPointerLeave={finishStroke}
      />
      <p className="signature-baseline">
        {lang === "es" ? "Firme en el recuadro de arriba" : "Sign in the box above"}
      </p>
      <div className="signature-actions">
        <span className={"signature-status" + (hasSignature ? " is-signed" : "")}>
          {hasSignature
            ? lang === "es"
              ? "Firma capturada"
              : "Signature captured"
            : lang === "es"
            ? "Aún no ha firmado"
            : "Not yet signed"}
        </span>
        <button type="button" className="signature-clear-btn" onClick={clearSignature}>
          {lang === "es" ? "Borrar" : "Clear"}
        </button>
      </div>
    </div>
  );
};

const LandingPage = () => {
  const [questions, setQuestions] = useState([]);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [validationError, setValidationError] = useState("");

  // step: "screening" | "info" | "consent"
  const [step, setStep] = useState("screening");

  const [patientData, setPatientData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    dateOfBirth: "",
    city: "",
    country: "USA",
    zip: "",
  });

  // HIPAA consent can be signed by drawing or by typing a full legal name.
  // Both values are kept independently so switching methods doesn't lose
  // whatever the person already entered in the other one. Whichever
  // method is active at submit time is what gets sent as signatureImage.
  const [signatureMethod, setSignatureMethod] = useState("draw"); // "draw" | "type"
  const [drawnSignature, setDrawnSignature] = useState(""); // PNG data URL
  const [typedSignature, setTypedSignature] = useState(""); // full legal name

  // Communication consent is a frontend-only gate — it is NOT sent to
  // the API, per current requirements. Only the HIPAA signature is.
  const [communicationConsent, setCommunicationConsent] = useState(false);

  const hasHipaaConsent =
    signatureMethod === "draw" ? Boolean(drawnSignature) : typedSignature.trim().length > 0;

  const [userLang] = useState(detectLanguage);

useEffect(() => {
  captureTracking(); // save UTMs / fbclid / Meta IDs / landing URL for the session
  loadQuestions();
}, []);
  const loadQuestions = async () => {
    try {
      setLoading(true);
      setError("");

      const structure = await getSurveyStructure(SURVEY_ID);
      const stages = structure?.stages || [];
      const stage1 = stages.find((s) => s.id === STAGE_1_ID);

      if (!stage1) {
        throw new Error("Primary Screening stage was not found in this survey.");
      }

      // Per the docs, a stage with questions: [] is valid and just means
      // "nothing configured yet" — not an error.
      const stageQuestions = stage1.questions || [];

      const sortedQuestions = [...stageQuestions].sort(
        (a, b) => (a.order || 0) - (b.order || 0)
      );
      setQuestions(sortedQuestions);

      const initialFormData = {};
      sortedQuestions.forEach((q) => {
        initialFormData[q.id] = q.questionType === "Checkbox" ? [] : "";
      });
      setFormData(initialFormData);
    } catch (err) {
      console.error("Survey loading error:", err);
      setError(err.message || "Unable to load survey.");
    } finally {
      setLoading(false);
    }
  };

  const handlePatientChange = (e) => {
    const { name, value } = e.target;
    setPatientData((prev) => ({ ...prev, [name]: value }));
    setValidationError("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setValidationError("");
  };

  const handleCheckboxChange = (questionId, englishValue, checked) => {
    setFormData((prev) => {
      const existing = prev[questionId] || [];
      return {
        ...prev,
        [questionId]: checked
          ? [...existing, englishValue]
          : existing.filter((i) => i !== englishValue),
      };
    });
    setValidationError("");
  };

  // Pick the language block for a question; both are always present
  // per the docs, choose based on the patient's selected language.
  const getLangBlock = (question) =>
    userLang === "es" ? question.spanish : question.english;

  // English block is used as the canonical stored value for choice-based
  // questions, regardless of which language is displayed.
  const getEnglishChoices = (question) => question.english?.choices || [];
  const getDisplayChoices = (question) => getLangBlock(question)?.choices || [];
  const getQuestionText = (question) => getLangBlock(question)?.questionText || "";

  const isQuestionAnswered = (question) => {
    const value = formData[question.id];
    if (question.isRequired === false) return true;
    if (question.questionType === "Checkbox") {
      return Array.isArray(value) && value.length > 0;
    }
    return value !== undefined && value !== null && value !== "";
  };

  const renderQuestion = (question, index) => {
    const questionId = question.id;
    const questionText = getQuestionText(question);
    const questionType = question.questionType;
    const required = question.isRequired;
    const displayChoices = getDisplayChoices(question);
    const englishChoices = getEnglishChoices(question);
    const value = formData[questionId];

    return (
      <div className="question-item" key={questionId}>
        <div className="question-head">
          <span className="question-number">{index + 1}</span>
          <p className="question">
            {questionText}
            {required && (
              <span className="required-mark" aria-hidden="true">
                *
              </span>
            )}
          </p>
        </div>

        {(questionType === "ShortText" || questionType === "Text") && (
          <input
            type="text"
            name={questionId}
            value={value || ""}
            onChange={handleChange}
            className="text-input"
            aria-label={questionText}
          />
        )}

        {questionType === "LongText" && (
          <textarea
            name={questionId}
            value={value || ""}
            onChange={handleChange}
            className="text-input"
            rows={5}
            aria-label={questionText}
          />
        )}

        {questionType === "Dropdown" && (
          <select
            name={questionId}
            value={value || ""}
            onChange={handleChange}
            className="text-input"
            aria-label={questionText}
          >
            <option value="" disabled>
              {userLang === "es" ? "Seleccione una opción" : "Select an option"}
            </option>
            {displayChoices.map((label, i) => {
              const englishValue = englishChoices[i] ?? label;
              return (
                <option key={englishValue} value={englishValue}>
                  {label}
                </option>
              );
            })}
          </select>
        )}

        {questionType === "Radio" && (
          <fieldset className="options-group">
            <legend className="sr-only">{questionText}</legend>
            <span className="instruction">
              {userLang === "es" ? "Seleccione uno" : "Select one"}
            </span>
            <div className="options-list">
              {displayChoices.map((label, i) => {
                const englishValue = englishChoices[i] ?? label;
                return (
                  <label className="option" key={englishValue}>
                    <input
                      type="radio"
                      name={questionId}
                      value={englishValue}
                      checked={value === englishValue}
                      onChange={handleChange}
                    />
                    <span>{label}</span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        )}

        {questionType === "Checkbox" && (
          <fieldset className="options-group">
            <legend className="sr-only">{questionText}</legend>
            <span className="instruction">
              {userLang === "es" ? "Seleccione todas las que apliquen" : "Select all that apply"}
            </span>
            <div className="options-list">
              {displayChoices.map((label, i) => {
                const englishValue = englishChoices[i] ?? label;
                return (
                  <label className="option" key={englishValue}>
                    <input
                      type="checkbox"
                      value={englishValue}
                      checked={(value || []).includes(englishValue)}
                      onChange={(e) =>
                        handleCheckboxChange(questionId, englishValue, e.target.checked)
                      }
                    />
                    <span>{label}</span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        )}

        {questionType === "Rating" && (
          <fieldset className="options-group">
            <legend className="sr-only">{questionText}</legend>
            <div className="rating-container">
              {[1, 2, 3, 4, 5].map((rating) => (
                <label key={rating}>
                  <input
                    type="radio"
                    name={questionId}
                    value={rating}
                    checked={String(value) === String(rating)}
                    onChange={handleChange}
                  />
                  {rating}
                </label>
              ))}
            </div>
          </fieldset>
        )}

        {questionType === "Date" && (
          <input
            type="date"
            name={questionId}
            value={value || ""}
            onChange={handleChange}
            className="text-input"
            aria-label={questionText}
          />
        )}
      </div>
    );
  };

  // ---- Screening step: all questions on one form, one Continue button ----

  const handleScreeningContinue = () => {
    const allAnswered = questions.every(isQuestionAnswered);

    if (!allAnswered) {
      setValidationError(
        userLang === "es"
          ? "Por favor responda todas las preguntas obligatorias antes de continuar."
          : "Please answer all required questions before continuing."
      );
      return;
    }

    setValidationError("");
    setStep("info");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ---- Patient info step navigation ----

  const handleInfoContinue = () => {
    if (!patientData.firstName.trim()) {
      setValidationError(
        userLang === "es" ? "El nombre es obligatorio." : "First name is required."
      );
      return;
    }

    setValidationError("");
    setStep("consent");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleInfoBack = () => {
    setValidationError("");
    setStep("screening");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ---- Consent step ----

  const handleConsentBack = () => {
    setValidationError("");
    setStep("info");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Submits patient info + every answer in one API call. The HIPAA
  // signature (drawn or typed) is sent as signatureImage; communication
  // consent is checked client-side only and is never sent to the API.
  const submitAllAnswers = async () => {
    try {
      setSubmitting(true);
      setValidationError("");

      if (!hasHipaaConsent) {
        throw new Error(
          userLang === "es"
            ? "Debe firmar o escribir su nombre para el consentimiento HIPAA."
            : "Please sign or type your name for the HIPAA consent to continue."
        );
      }

      if (!communicationConsent) {
        throw new Error(
          userLang === "es"
            ? "Debe aceptar el consentimiento de comunicación para continuar."
            : "Please accept the communication consent to continue."
        );
      }

const rawSignatureImage =
  signatureMethod === "draw"
    ? drawnSignature
    : await renderTypedSignatureToDataUrl(typedSignature.trim());

// Strip the "data:image/png;base64," header so only pure, correctly
// padded base64 is sent. Split on the first comma rather than a
// fixed-length substring — a hard-coded prefix length is fragile and
// is what causes base64 padding/"remainder" errors downstream.
const signatureImage = rawSignatureImage.includes(",")
  ? rawSignatureImage.split(",")[1]
  : rawSignatureImage;

      const answers = questions.map((question) => {
        const value = formData[question.id];
        return {
          stageId: STAGE_1_ID,
          questionId: question.id,
          answer: Array.isArray(value) ? value.join("; ") : value,
        };
      });

     const response = await submitSurveyResponse(SURVEY_ID, {
  firstName: patientData.firstName,
  lastName: patientData.lastName,
  email: patientData.email,
  phone: patientData.phone,
  dateOfBirth: patientData.dateOfBirth,
  city: patientData.city,
  country: patientData.country,
  zip: patientData.zip,
  signatureImage,
  answers,
  tracking: {
    ...getTracking(),
    website_submission_id: getSubmissionId(),
  },
});

      if (!response?.success) {
        throw new Error(
          response?.error ||
            (userLang === "es"
              ? "No se pudo guardar su respuesta. Inténtelo de nuevo."
              : "Unable to save your answer. Please try again.")
        );
      }
clearSubmissionId(); // next candidate gets a fresh ID
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error("Submission failed:", err);
      setValidationError(
        err.message ||
          (userLang === "es"
            ? "No se pudo guardar su respuesta. Inténtelo de nuevo."
            : "Unable to save your answer. Please try again.")
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Full-screen states ----

  if (loading) {
    return (
      <div className="survey-page">
        <div className="survey-container">
          <div className="state-card">
            <div className="spinner" />
            <p>{userLang === "es" ? "Cargando encuesta..." : "Loading survey..."}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="survey-page">
        <div className="survey-container">
          <div className="state-card">
            <div className="error-icon" aria-hidden="true">
              !
            </div>
            <h2>{userLang === "es" ? "No se pudo cargar la encuesta" : "We couldn't load the survey"}</h2>
            <p>{error}</p>
            <button type="button" className="retry-btn" onClick={loadQuestions}>
              {userLang === "es" ? "Intentar de nuevo" : "Try again"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="survey-page">
        <div className="survey-container">
          <div className="state-card success">
            <div className="success-icon" aria-hidden="true">
              ✓
            </div>
            <h1>{userLang === "es" ? "Gracias" : "Thank you"}</h1>
            <p>
              {userLang === "es"
                ? "Su encuesta ha sido enviada con éxito."
                : "Your survey has been submitted successfully."}
            </p>
            <p className="success-subtext">
              {userLang === "es"
                ? "Agradecemos que se haya tomado el tiempo de compartir sus comentarios. Un miembro de nuestro equipo se pondrá en contacto si corresponde."
                : "Thank you for taking the time to share your information. A member of our team will follow up if you're a fit for the study."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ---- Consent step ----

  if (step === "consent") {
    return (
      <div className="survey-page">
        <div className="survey-container">
          <div className="hero-band">
            <PulseLine />
            <p className="hero-kicker">IRB-approved research screening</p>
            <h1>Consent</h1>
            <p>Please review and provide both consents below to submit your survey.</p>
          </div>

          <Stepper activeIndex={2} />

          <div className="question-card consent-card">
            <div className="consent-block">
              <h2 className="consent-block-title">
                {userLang === "es" ? "Consentimiento HIPAA" : "HIPAA consent"}
                <span className="required-mark">*</span>
              </h2>
              <p className="consent-block-body">
                {userLang === "es"
                  ? "Al firmar a continuación, usted autoriza el uso y divulgación de su información de salud según se describe en el Aviso de Prácticas de Privacidad (HIPAA)."
                  : "By signing below, you authorize the use and disclosure of your health information as described in the HIPAA Notice of Privacy Practices."}
              </p>

              <div className="signature-method-toggle" role="tablist" aria-label="Signature method">
                <button
                  type="button"
                  role="tab"
                  aria-selected={signatureMethod === "draw"}
                  className={"method-btn" + (signatureMethod === "draw" ? " is-active" : "")}
                  onClick={() => setSignatureMethod("draw")}
                >
                  {userLang === "es" ? "Dibujar" : "Draw"}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={signatureMethod === "type"}
                  className={"method-btn" + (signatureMethod === "type" ? " is-active" : "")}
                  onClick={() => setSignatureMethod("type")}
                >
                  {userLang === "es" ? "Escribir" : "Type"}
                </button>
              </div>

              {signatureMethod === "draw" ? (
                <SignaturePad
                  hasError={Boolean(validationError) && !drawnSignature}
                  initialDataUrl={drawnSignature}
                  onChange={setDrawnSignature}
                  lang={userLang}
                />
              ) : (
                <div className="signature-type-wrap">
                  <label className="field-label" htmlFor="typedSignature">
                    {userLang === "es" ? "Escriba su nombre legal completo" : "Type your full legal name"}
                  </label>
                  <input
                    id="typedSignature"
                    type="text"
                    className={
                      "text-input" +
                      (validationError && !typedSignature.trim() ? " has-error" : "")
                    }
                    placeholder="Jane A. Doe"
                    value={typedSignature}
                    onChange={(e) => {
                      setTypedSignature(e.target.value);
                      setValidationError("");
                    }}
                  />
                  {typedSignature.trim() && (
                    <p className="signature-type-preview">{typedSignature}</p>
                  )}
                  <p className="signature-baseline">
                    {userLang === "es"
                      ? "Al escribir su nombre, usted acepta que esto constituye su firma legal."
                      : "By typing your name, you agree this constitutes your legal signature."}
                  </p>
                </div>
              )}
            </div>

            <div className="consent-block">
              <h2 className="consent-block-title">
                {userLang === "es" ? "Consentimiento de comunicación" : "Communication consent"}
                <span className="required-mark">*</span>
              </h2>
              <label className="option consent-option">
                <input
                  type="checkbox"
                  checked={communicationConsent}
                  onChange={(e) => {
                    setCommunicationConsent(e.target.checked);
                    setValidationError("");
                  }}
                />
                <span>
                  {userLang === "es"
                    ? "Acepto ser contactado/a por teléfono, correo electrónico o mensaje de texto con respecto a esta encuesta y posible seguimiento."
                    : "I agree to be contacted by phone, email, or text message regarding this survey and potential follow-up."}
                </span>
              </label>
            </div>

            {validationError && <ValidationMessage>{validationError}</ValidationMessage>}

            <div className="navigation-section">
              <button type="button" className="back-btn" onClick={handleConsentBack} disabled={submitting}>
                <ChevronLeft />
                {userLang === "es" ? "Atrás" : "Back"}
              </button>

              <button
                type="button"
                className="submit-btn"
                disabled={submitting || !hasHipaaConsent || !communicationConsent}
                onClick={submitAllAnswers}
              >
                {submitting
                  ? userLang === "es"
                    ? "Guardando..."
                    : "Saving..."
                  : userLang === "es"
                  ? "Enviar encuesta"
                  : "Submit survey"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- Patient info step ----

  if (step === "info") {
    return (
      <div className="survey-page">
        <div className="survey-container">
          <div className="hero-band">
            <PulseLine />
            <p className="hero-kicker">IRB-approved research screening</p>
            <h1>Almost done</h1>
            <p>Enter your information so we can follow up about next steps.</p>
          </div>

          <Stepper activeIndex={1} />

          <div className="question-card patient-card">
            <h2>Your information</h2>
            <p className="patient-subtext">
              {userLang === "es"
                ? "Usaremos estos datos únicamente para dar seguimiento a su participación."
                : "We'll only use this to follow up about your participation."}
            </p>

            <div className="patient-grid">
              <div className="field-group">
                <label className="field-label" htmlFor="firstName">
                  First name<span className="required-mark">*</span>
                </label>
                <input
                  id="firstName"
                  type="text"
                  name="firstName"
                  placeholder="John"
                  value={patientData.firstName}
                  onChange={handlePatientChange}
                  className="text-input"
                />
              </div>
              <div className="field-group">
                <label className="field-label" htmlFor="lastName">
                  Last name
                </label>
                <input
                  id="lastName"
                  type="text"
                  name="lastName"
                  placeholder="Doe"
                  value={patientData.lastName}
                  onChange={handlePatientChange}
                  className="text-input"
                />
              </div>
              <div className="field-group">
                <label className="field-label" htmlFor="dateOfBirth">
                  Date of birth
                </label>
                <input
                  id="dateOfBirth"
                  type="date"
                  name="dateOfBirth"
                  value={patientData.dateOfBirth}
                  onChange={handlePatientChange}
                  className="text-input"
                />
              </div>
              <div className="field-group">
                <label className="field-label" htmlFor="email">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  placeholder="you@example.com"
                  value={patientData.email}
                  onChange={handlePatientChange}
                  className="text-input"
                />
              </div>
              <div className="field-group">
                <label className="field-label" htmlFor="phone">
                  Phone
                </label>
                <input
                  id="phone"
                  type="tel"
                  name="phone"
                  placeholder="(555) 123-4567"
                  value={patientData.phone}
                  onChange={handlePatientChange}
                  className="text-input"
                />
              </div>
              <div className="field-group">
                <label className="field-label" htmlFor="city">
                  City
                </label>
                <input
                  id="city"
                  type="text"
                  name="city"
                  placeholder="Los Angeles"
                  value={patientData.city}
                  onChange={handlePatientChange}
                  className="text-input"
                />
              </div>
              <div className="field-group">
                <label className="field-label" htmlFor="zip">
                  Zip code
                </label>
                <input
                  id="zip"
                  type="text"
                  name="zip"
                  placeholder="90210"
                  value={patientData.zip}
                  onChange={handlePatientChange}
                  className="text-input"
                />
              </div>
              <div className="field-group">
                <label className="field-label" htmlFor="country">
                  Country
                </label>
                <input
                  id="country"
                  type="text"
                  name="country"
                  value={patientData.country}
                  onChange={handlePatientChange}
                  className="text-input"
                />
              </div>
            </div>

            {validationError && <ValidationMessage>{validationError}</ValidationMessage>}

            <div className="navigation-section">
              <button type="button" className="back-btn" onClick={handleInfoBack} disabled={submitting}>
                <ChevronLeft />
                {userLang === "es" ? "Atrás" : "Back"}
              </button>

              <button type="button" className="next-btn" onClick={handleInfoContinue} disabled={submitting}>
                {userLang === "es" ? "Continuar" : "Continue"}
                <ChevronRight />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- Screening step (default): all questions on one form ----

  // ---- Screening step (default): all questions in ONE card ----

  return (
    <div className="survey-page">
      <div className="survey-container">
        <div className="hero-band">
          <PulseLine />
          <p className="hero-kicker">IRB-approved research screening</p>
          <h1>Primary screening</h1>
          <p>
            {userLang === "es"
              ? "Gracias por tomarse el tiempo de participar. Esto toma solo unos minutos."
              : "Thank you for taking the time to participate. This takes just a few minutes."}
          </p>
        </div>

        <Stepper activeIndex={0} />

        <div className="question-card questions-card">
          <div className="questions-card-head">
            <h2>{userLang === "es" ? "Preguntas de selección" : "Screening questions"}</h2>
            <p>
              {userLang === "es"
                ? "Los campos marcados con * son obligatorios."
                : "Fields marked with * are required."}
            </p>
          </div>

          {questions.map((question, index) => renderQuestion(question, index))}

          {validationError && <ValidationMessage>{validationError}</ValidationMessage>}

          <div className="navigation-section">
            <button type="button" className="next-btn" onClick={handleScreeningContinue}>
              {userLang === "es" ? "Continuar" : "Continue"}
              <ChevronRight />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;